import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfirmModal, StepIcon } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { showSnackbar } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { setQueuedPrompt } from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';
import {
  getActiveLubavSession,
  getSessionState,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  buildCategoryIdByCode,
  useComputeMutation,
  useCreateSubmissionMutation,
  useFinalizeSubmissionMutation,
  useGetMostRecentQuery,
  useGetScoreCategoriesQuery,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.api';
import {
  defaultTargetScore,
  testsInSelection,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.constants';
import {
  selectBoosterDraft,
  selectBoosterReportContent,
  selectBoosterReportFacts,
  selectBoosterStep,
  selectBoosterSubmissionId,
  setBoosterReportFacts,
  setBoosterReportRequestFloor,
  setBoosterStep,
  setBoosterSubmissionId,
  startFreshRun,
  updateIntake,
  updateTargets,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.slice';
import {
  BoosterDraft,
  ReportFacts,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';
import { revealElement, scrollPageToTop } from '@backrs_ui/grow-ui/src/utils/scrollReveal';

import { Box, Button, SxProps, Theme } from '@mui/material';

import { BoosterLoadError } from './components/BoosterLoadError';
import { BoosterLoading } from './components/BoosterLoading';
import { BoosterReportLoading } from './components/BoosterReportLoading';
import { NoScoreModal } from './components/NoScoreModal';
import { TestBoosterBanner } from './components/TestBoosterBanner';
import { useBoosterReport } from './hooks/useBoosterReport';
import { useBoosterResume } from './hooks/useBoosterResume';
import { boosterPageSx } from './satActBooster.styles';
import { useAgentContext } from './SatActBoosterPageContainer.agentContext';
import { IntakeStep } from './steps/IntakeStep';
import { ScheduleStep } from './steps/ScheduleStep';
import { ScoreInputStep } from './steps/ScoreInputStep';
import { TargetsStep } from './steps/TargetsStep';
import { getLocalInsufficientReasons, insufficientReasonCopy } from './utils/insufficientSchedule';
import {
  actCompositeMismatch,
  buildSteps,
  canAdvance,
  getNextStep,
  getPreviousStep,
  getTargetsErrors,
  StepKey,
} from './utils/stepNavigation';

const STRINGS = appStrings.modules.satActBooster;

// The single Goals-step required field; a failed submit scrolls to it.
const firstTargetsErrorAnchor = (draft: BoosterDraft): string | null =>
  getTargetsErrors(draft.targets).targetScore ? 'booster-target-score-decrement' : null;

// First failing Schedule field, in top-to-bottom order, so a failed submit scrolls to it.
const firstScheduleErrorAnchor = (draft: BoosterDraft): string | null => {
  const errors = getTargetsErrors(draft.targets);

  if (errors.testDate) return 'booster-target-test-date';
  if (errors.startDate) return 'booster-study-start-date';
  if (errors.studyDays) return 'booster-study-days';

  return null;
};

export const SatActBoosterPageContainer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const draft = useAppSelector(selectBoosterDraft);
  const step = useAppSelector(selectBoosterStep);
  const reportFacts = useAppSelector(selectBoosterReportFacts);
  const reportContent = useAppSelector(selectBoosterReportContent);
  const submissionId = useAppSelector(selectBoosterSubmissionId);
  const { isUserOrGuest, loggedIn } = useAuth();

  // Users and guests both resume their own saved submission (guests own theirs by guest token),
  // so a refresh restores the wizard alongside the recalled chat.
  const {
    data: mostRecent,
    isLoading: isRecallLoading,
    isError: isRecallError,
    error: recallError,
    refetch: refetchRecall,
  } = useGetMostRecentQuery(undefined, {
    skip: !isUserOrGuest,
  });
  // A 404 means "no submission yet" (start fresh); any other error is a real failure to surface.
  const recallFailed = isRecallError && (recallError as { status?: number })?.status !== 404;
  const [compute, { isLoading: isComputing }] = useComputeMutation();
  const [createSubmission, { isLoading: isCreating }] = useCreateSubmissionMutation();
  const [finalizeSubmission, { isLoading: isFinalizing }] = useFinalizeSubmissionMutation();

  // Resolve subscore codes to DB ids so the reporting-category breakdown persists.
  const { act: needsAct, sat: needsSat } = testsInSelection(draft.whichTests);
  const {
    data: actCategories,
    isSuccess: actCategoriesLoaded,
    isError: actCategoriesFailed,
    refetch: refetchActCategories,
  } = useGetScoreCategoriesQuery('act', { skip: !needsAct });
  const {
    data: satCategories,
    isSuccess: satCategoriesLoaded,
    isError: satCategoriesFailed,
    refetch: refetchSatCategories,
  } = useGetScoreCategoriesQuery('sat', { skip: !needsSat });
  const categoryIdByCode = useMemo(
    () => buildCategoryIdByCode([...(actCategories ?? []), ...(satCategories ?? [])]),
    [actCategories, satCategories],
  );
  // Subscores resolve to ids only once every needed test's categories load.
  const categoriesLoaded = (!needsAct || actCategoriesLoaded) && (!needsSat || satCategoriesLoaded);
  const categoriesFailed = (needsAct && actCategoriesFailed) || (needsSat && satCategoriesFailed);

  // Without this the categories error snackbar is a dead end — nothing else refetches them.
  const retryCategories = useCallback(() => {
    if (needsAct && actCategoriesFailed) refetchActCategories();
    if (needsSat && satCategoriesFailed) refetchSatCategories();
  }, [
    needsAct,
    needsSat,
    actCategoriesFailed,
    satCategoriesFailed,
    refetchActCategories,
    refetchSatCategories,
  ]);

  const steps = useMemo(() => buildSteps(draft.whichTests), [draft.whichTests]);
  const stepIndex = steps.indexOf(step);

  // A long step leaves the scroller deep in the page, so the next step would open mid-form.
  // A failed Next returns early and never lands here, so its scroll-to-error still wins.
  useEffect(() => {
    scrollPageToTop();
  }, [step]);
  // The step whose Next was pressed while invalid, so that step alone reveals its errors —
  // latching it globally would paint the next step's untouched fields red on arrival.
  const [submitAttemptedStep, setSubmitAttemptedStep] = useState<StepKey | null>(null);
  // Gates the "proceed anyway" confirm shown when a valid but light schedule leaves Targets.
  const [scheduleConfirmOpen, setScheduleConfirmOpen] = useState(false);
  // Gates the "double-check your composite" confirm shown when the ACT composite disagrees
  // with the section scores. A warning, never a block — superscores are legitimate.
  const [compositeConfirmOpen, setCompositeConfirmOpen] = useState(false);
  // The local (pre-send) reasons that would trip the confirm; drives its body copy too.
  const scheduleReasons = useMemo(
    () => getLocalInsufficientReasons(draft.targets),
    [draft.targets],
  );

  useAgentContext(step, reportFacts);
  // `settled` gates the wizard so it never starts while a resume is still pending.
  const { settled: recallSettled } = useBoosterResume({
    isRecallLoading,
    recallFailed,
    mostRecent,
  });

  const { awaitingReport, reportReady } = useBoosterReport();

  // Declared after useAgentContext so the facts register before this prompt is queued.
  // Keyed on facts identity so a fresh run re-queues.
  const queuedFactsRef = useRef<ReportFacts | null>(null);
  const activeSession = useAppSelector(getActiveLubavSession);
  const chatMessageCount = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav.officeHoursMessages.length,
  );

  useEffect(() => {
    // Never queue a live report turn when a report already exists (a resumed/frozen run,
    // or stepping Back onto REPORT) — that would generate a second report under the first.
    if (!reportFacts || reportContent || queuedFactsRef.current === reportFacts) return;

    queuedFactsRef.current = reportFacts;
    // Everything already in the chat predates this request, including any earlier run's report.
    dispatch(setBoosterReportRequestFloor(chatMessageCount));
    // The score report renders as a standard office-hours turn via a prompt button. Queued once the
    // facts are computed (leaving the last score step); Targets opens behind it once it lands.
    dispatch(
      setQueuedPrompt({
        label: STRINGS.nav.reportSeamPrompt,
        prompt: STRINGS.nav.reportSeamPrompt,
        promptButtonId: 'booster_score_report',
        timestamp: Date.now(),
      }),
    );
  }, [reportFacts, reportContent, chatMessageCount, dispatch]);

  const runReport = async () => {
    // Adopt the computed stronger test + recommended goal; the report prompt is queued separately.
    const facts = await compute({ draft }).unwrap();

    dispatch(setBoosterReportFacts(facts));
    dispatch(
      updateTargets({
        targetTest: facts.test,
        ...(draft.targets.targetScore === null
          ? {
              targetScore: defaultTargetScore(
                facts.test,
                facts.currentScore,
                facts.recommendedGoal,
              ),
            }
          : {}),
      }),
    );
  };

  // Persist the run once its report markdown is captured — the report can't be recomputed,
  // so it must survive a refresh. Keyed on the markdown so a fresh run re-creates a row.
  const createdForReportRef = useRef<string | null>(null);
  // `categoriesFailed` flips back to false while a refetch is pending, so an unlatched retry
  // would re-arm itself and spin for as long as the endpoint stays down.
  const retriedForReportRef = useRef<string | null>(null);
  // Surfaced on Targets: the run is only in memory until the create lands, so a failure the
  // student can't see means a refresh silently discards their intake, scores, and report.
  const [saveDeferred, setSaveDeferred] = useState(false);

  useEffect(() => {
    if (!reportContent) {
      createdForReportRef.current = null;
      retriedForReportRef.current = null;

      return;
    }
    // A resumed run already has an id; don't create until the resume decision settles.
    if (submissionId || !recallSettled) return;
    if (createdForReportRef.current === reportContent) return;
    // Wait for categories to resolve, else the subscore breakdown is silently dropped; surface load
    // failures and retry them once, so a transient failure doesn't strand the run unsaved.
    if (!categoriesLoaded) {
      if (categoriesFailed && retriedForReportRef.current !== reportContent) {
        retriedForReportRef.current = reportContent;
        dispatch(showSnackbar({ message: STRINGS.report.categoriesError, severity: 'error' }));
        retryCategories();
      }

      return;
    }

    createdForReportRef.current = reportContent;
    createSubmission({ draft, categoryIdByCode, reportContent })
      .unwrap()
      .then((res) => {
        setSaveDeferred(false);
        dispatch(setBoosterSubmissionId(res.id));
      })
      .catch((error) => {
        // Leave the run unsaved and retry at completion rather than trap the user, but say so:
        // an unobserved failure here loses the whole run on a refresh.
        console.error('[test-booster] failed to persist submission', error);
        setSaveDeferred(true);
        createdForReportRef.current = null;
      });
  }, [
    reportContent,
    submissionId,
    recallSettled,
    categoriesLoaded,
    categoriesFailed,
    retryCategories,
    draft,
    categoryIdByCode,
    createSubmission,
    dispatch,
  ]);

  if (isRecallLoading) {
    return <BoosterLoading />;
  }

  // A real recall failure must not silently start a fresh run over a resumable one.
  if (recallFailed) {
    return <BoosterLoadError onRetry={refetchRecall} />;
  }

  // Targets is built around the report, so it waits rather than opening behind the stream.
  if (awaitingReport) {
    return <BoosterReportLoading ready={reportReady} />;
  }

  // Finalize the run at completion. Falls back to a create if it was never persisted; a 409
  // means it is already complete, which is not an error. Returns true when the run is
  // finalized (so the caller may advance to results), false when it must stay put.
  const finalizeAtCompletion = async (): Promise<boolean> => {
    try {
      let id = submissionId;

      // Without the category ids the breakdown resolves to nothing, saving as an empty one.
      if (draft.ownsSubscores && !categoriesLoaded) {
        dispatch(showSnackbar({ message: STRINGS.report.categoriesError, severity: 'error' }));
        retryCategories();

        return false;
      }

      if (!id) {
        const created = await createSubmission({ draft, categoryIdByCode, reportContent }).unwrap();

        id = created.id;
        dispatch(setBoosterSubmissionId(id));
      }

      // Omitted rather than passed as null: the row already holds a report the client can't recompute, and sending null would overwrite it with nothing.
      await finalizeSubmission({
        id,
        draft,
        categoryIdByCode,
        reportContent: reportContent ?? undefined,
      }).unwrap();

      return true;
    } catch (error) {
      if ((error as { status?: number })?.status === 409) return true;

      dispatch(showSnackbar({ message: STRINGS.report.error, severity: 'error' }));

      return false;
    }
  };

  // Finalize the run, then hand off to the interim animation route, which forwards to the plan.
  // Finalizing here (not there) keeps that route free of side effects, so re-entering it on a Back
  // press can't re-complete the run. Both the animation and the plan read the finalized submission
  // from the server, so the slice is free to reset behind them.
  const proceedToPlan = async () => {
    const finalized = await finalizeAtCompletion();

    if (finalized) {
      navigate(APP_ROUTE.SAT_ACT_BOOSTER_GENERATING);
      dispatch(startFreshRun());
    }
  };

  const handleNext = async () => {
    if (!canAdvance(step, draft, loggedIn)) {
      // Enabled on Goals/Schedule so the click reveals what's missing instead of a dead button, then scrolls the first offending field into view since the form runs past the fold.
      if (step === 'TARGETS' || step === 'SCHEDULE') {
        setSubmitAttemptedStep(step);

        const anchorId =
          step === 'TARGETS' ? firstTargetsErrorAnchor(draft) : firstScheduleErrorAnchor(draft);

        if (anchorId) revealElement(document.getElementById(anchorId), 'center');
      }

      return;
    }

    if (step === 'SCHEDULE') {
      // A valid but light schedule adds friction, never a gate: confirm once, then continue.
      if (scheduleReasons.length > 0) {
        setScheduleConfirmOpen(true);

        return;
      }

      await proceedToPlan();

      return;
    }

    // Leaving the ACT scores: warn (never gate) when the composite disagrees with the
    // sections, so a mistyped composite gets a second look before the report is built.
    if (step === 'SCORE_ACT' && actCompositeMismatch(draft.actScores)) {
      setCompositeConfirmOpen(true);

      return;
    }

    await advanceStep();
  };

  // Only the step that actually computes a report; revisiting a score checkpoint without editing
  // leaves the existing one intact, and Next there just advances. With no test selection there are
  // no score screens, so Intake's own next step is Targets.
  const computesReport =
    (step === 'SCORE_ACT' || step === 'SCORE_SAT') &&
    getNextStep(steps, step) === 'TARGETS' &&
    reportFacts === null;

  // Advance out of the current input step, computing the report on the seam into Targets.
  const advanceStep = async () => {
    const next = getNextStep(steps, step);

    // On failure stay put rather than advance with no report.
    if (computesReport) {
      try {
        await runReport();
      } catch {
        dispatch(showSnackbar({ message: STRINGS.report.error, severity: 'error' }));

        return;
      }
    }

    if (next) dispatch(setBoosterStep(next));
  };

  // Back only moves between the pre-report input steps (About-you ↔ Scores); once the report is
  // generated the wizard is forward-only, so there's no report state to unwind here.
  const handleBack = () => {
    const previous = getPreviousStep(steps, step);

    if (previous) dispatch(setBoosterStep(previous));
  };

  const nextLabel =
    step === 'SCHEDULE'
      ? STRINGS.nav.buildPlan
      : computesReport
        ? STRINGS.nav.analyzeScores
        : STRINGS.nav.next;
  const visibleSteps = steps;
  const visibleIndex = stepIndex;
  // Checkpoints already behind the user are always revisitable. Jumping ahead needs a report
  // (so the score steps have been walked at least once) *and* every step being skipped to still
  // pass its own gate — otherwise the jump would bypass the validation the Next button enforces.
  // Editing a score clears the report, which closes forward jumps until Next recomputes it.
  const reportGenerated = reportFacts !== null;
  const isStepReachable = (index: number) =>
    index !== visibleIndex &&
    (index < visibleIndex ||
      (reportGenerated &&
        visibleSteps.slice(visibleIndex, index).every((key) => canAdvance(key, draft, loggedIn))));
  const goToStep = (index: number, key: StepKey) =>
    isStepReachable(index) ? () => dispatch(setBoosterStep(key)) : undefined;
  // Forward-only once the report is generated: no Back from the intake step (first) or the
  // post-report Goals/Schedule steps. Back only moves between the score input screens; use the
  // stepper checkpoints to revisit completed steps.
  const canGoBack = stepIndex > 0 && step !== 'TARGETS' && step !== 'SCHEDULE';

  return (
    <Box sx={boosterPageSx}>
      <TestBoosterBanner />
      <Box sx={timelineSx}>
        <Box sx={checkpointsSx}>
          {visibleSteps.slice(0, visibleIndex + 1).map((key, index) => (
            <StepIcon
              key={key}
              id={`booster-checkpoint-${key}`}
              label={STRINGS.steps[key]}
              variant={index === visibleIndex ? 'current-previous' : 'done'}
              onClick={goToStep(index, key)}
            />
          ))}
        </Box>

        {step === 'INTAKE' && <IntakeStep />}
        {step === 'SCORE_ACT' && <ScoreInputStep test="act" />}
        {step === 'SCORE_SAT' && <ScoreInputStep test="sat" />}
        {step === 'TARGETS' && (
          <TargetsStep
            reportFacts={reportFacts}
            submitAttempted={submitAttemptedStep === 'TARGETS'}
            saveDeferred={saveDeferred}
          />
        )}
        {step === 'SCHEDULE' && (
          <ScheduleStep
            reportFacts={reportFacts}
            submitAttempted={submitAttemptedStep === 'SCHEDULE'}
            saveDeferred={saveDeferred}
          />
        )}

        {visibleIndex < visibleSteps.length - 1 && (
          <Box sx={checkpointsSx}>
            {visibleSteps.slice(visibleIndex + 1).map((key, offset) => {
              const index = visibleIndex + 1 + offset;

              return (
                <StepIcon
                  key={key}
                  id={`booster-checkpoint-${key}`}
                  label={STRINGS.steps[key]}
                  variant="next"
                  onClick={goToStep(index, key)}
                />
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={canGoBack ? footerSx : footerEndSx}>
        {canGoBack && (
          <Button
            id="booster-step-back"
            variant="contained"
            size="lg"
            hierarchy="Secondary gray"
            onClick={handleBack}
          >
            {STRINGS.nav.back}
          </Button>
        )}
        <Button
          id="booster-step-next"
          variant="contained"
          size="lg"
          hierarchy="Primary"
          disabled={
            (step !== 'TARGETS' && step !== 'SCHEDULE' && !canAdvance(step, draft, loggedIn)) ||
            isComputing ||
            isCreating ||
            isFinalizing
          }
          onClick={handleNext}
        >
          {nextLabel}
        </Button>
      </Box>

      {/* "Haven't taken yet" dead-ends intake; clearing it returns to the test question. */}
      <NoScoreModal
        open={draft.whichTests === 'none'}
        onClose={() => dispatch(updateIntake({ whichTests: null }))}
      />

      <ConfirmModal
        open={scheduleConfirmOpen}
        onClose={() => setScheduleConfirmOpen(false)}
        variant="warning"
        headerText={STRINGS.insufficientSchedule.calloutTitle}
        bodyText={scheduleReasons.map(insufficientReasonCopy).join(' ')}
        primaryAction={{
          id: 'booster-schedule-confirm-continue-btn',
          label: STRINGS.insufficientSchedule.confirmContinue,
          onClick: () => {
            setScheduleConfirmOpen(false);
            proceedToPlan();
          },
        }}
        secondaryAction={{
          id: 'booster-schedule-confirm-goback-btn',
          label: STRINGS.insufficientSchedule.confirmGoBack,
          onClick: () => setScheduleConfirmOpen(false),
        }}
      />

      <ConfirmModal
        open={compositeConfirmOpen}
        onClose={() => setCompositeConfirmOpen(false)}
        variant="warning"
        headerText={STRINGS.compositeCheck.title}
        bodyText={STRINGS.compositeCheck.body}
        primaryAction={{
          id: 'booster-composite-confirm-continue-btn',
          label: STRINGS.compositeCheck.confirmContinue,
          onClick: () => {
            setCompositeConfirmOpen(false);
            advanceStep();
          },
        }}
        secondaryAction={{
          id: 'booster-composite-confirm-goback-btn',
          label: STRINGS.compositeCheck.confirmGoBack,
          onClick: () => setCompositeConfirmOpen(false),
        }}
      />
    </Box>
  );
};

const timelineSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const checkpointsSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
};

const footerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-lg'],
  marginTop: backrs.spacing['spacing-md'],
});

const footerEndSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-lg'],
  marginTop: backrs.spacing['spacing-md'],
});
